import { OutlinedInput } from "@mui/material";
import { useFormat } from "@sinch/core/hooks/format";
import { isEmpty } from "ramda";
import React, { forwardRef, ReactElement, useEffect, useRef, useState } from "react";
import { FormControlWrapper, TextInputProps } from "@sinch/components";
import { useNumberInputValue } from "@sinch/components/form/hooks/useNumberInputValue";

export type CurrencyInputProps = Omit<TextInputProps<"number">, "value" | "onChange"> & {
  value: number;
  onChange: (val: number) => void;
};

export const CurrencyInputBase = ({
  label,
  FormHelperTextProps: formHelperTextProps,
  FormControlProps: formControlProps,
  helperText,
  errorMessage,
  error,
  required,
  infoTooltip,
  onChange,
  value,
  allowEmpty,
  ...props
}: CurrencyInputProps): ReactElement => {
  const [editMode, setEditMode] = useState(false);
  const ref = useRef<HTMLInputElement>();
  const { num } = useFormat();

  useEffect(() => {
    if (editMode && ref.current) {
      ref.current.select();
    }
  }, [editMode]);

  const {
    toClosestLimit,
    toNumber,
    value: numberValue,
  } = useNumberInputValue({
    value,
    allowEmpty,
    onChange: (e) => onChange(parseInt(e.target.value)),
    min: props.inputProps?.min,
    max: props.inputProps?.max,
  });

  return (
    <FormControlWrapper
      error={error}
      errorMessage={errorMessage}
      FormHelperTextProps={formHelperTextProps}
      helperText={helperText}
      infoTooltip={infoTooltip}
      label={label}
      required={required}
      {...formControlProps}
    >
      <OutlinedInput
        error={error}
        {...props}
        fullWidth
        inputProps={{
          sx: { textAlign: "right", "&.MuiInputBase-inputSizeSmall": { px: 1 } },
          ref: ref,
          ...props.inputProps,
        }}
        onBlur={(e) => {
          props?.onBlur?.(e);
          const newVal = toClosestLimit(e.target.value);
          onChange?.(isEmpty(newVal) ? 0 : parseFloat(newVal));
          setEditMode(false);
        }}
        onChange={(e) => {
          if (editMode) {
            const newVal = toNumber(e.target.value);
            onChange?.(isEmpty(newVal) ? 0 : parseFloat(newVal));
          }
        }}
        onFocus={(e) => {
          setEditMode(true);
          props.onFocus?.(e);
        }}
        sx={{ height: "100%", ...props.sx }}
        type={editMode ? "number" : "text"}
        value={editMode ? value : num.currency(numberValue as number)}
      />
    </FormControlWrapper>
  );
};

export const CurrencyInput = forwardRef(CurrencyInputBase) as (
  props: CurrencyInputProps
) => ReturnType<typeof CurrencyInputBase>;
